/**
 * NQG-RQ-31 — Cascading Theme → Subtheme → Sub-subtheme selector for one
 * portfolio's taxonomy. Selection at ANY depth is valid (no forced depth):
 * onChange fires with the deepest selected node (or null = no taxonomy
 * filter). Reused by the Question Bank filter, the Generator PDF drill-down
 * and the PDF placement dialog.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { portfolioService, TaxonomyNode, TAXONOMY_LEVEL_LABELS } from '../services/portfolio.service';

interface TaxonomyCascadeProps {
  portfolioId: string | null; // null = no portfolio chosen yet (cascade hidden)
  value: string | null; // deepest selected node id
  onChange: (nodeId: string | null, pathNames: string[]) => void;
  compact?: boolean; // smaller paddings (for filter bars)
}

export function TaxonomyCascade({ portfolioId, value, onChange, compact = false }: TaxonomyCascadeProps) {
  const { data: nodes = [] } = useQuery({
    queryKey: ['taxonomy', portfolioId],
    queryFn: () => portfolioService.getTaxonomy(portfolioId!),
    enabled: !!portfolioId,
    retry: false,
    staleTime: 60 * 1000,
  });

  const byParent = useMemo(() => {
    const map = new Map<string, TaxonomyNode[]>();
    for (const n of nodes) {
      const key = n.parentId ? String(n.parentId) : 'root';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(n);
    }
    return map;
  }, [nodes]);

  const byId = useMemo(() => new Map(nodes.map((n) => [String(n._id), n])), [nodes]);

  // Derive the 3-level selection from the deepest selected node
  const chain = useMemo(() => {
    const result: string[] = [];
    let current = value ? byId.get(value) : undefined;
    while (current) {
      result.unshift(String(current._id));
      current = current.parentId ? byId.get(String(current.parentId)) : undefined;
    }
    return result;
  }, [value, byId]);

  const [levels, setLevels] = useState<(string | null)[]>([null, null, null]);
  useEffect(() => {
    setLevels([chain[0] || null, chain[1] || null, chain[2] || null]);
  }, [chain.join('|'), portfolioId]);

  if (!portfolioId) return null;

  const pathNamesFor = (nodeId: string): string[] => {
    const names: string[] = [];
    let current = byId.get(nodeId);
    while (current) {
      names.unshift(current.name);
      current = current.parentId ? byId.get(String(current.parentId)) : undefined;
    }
    return names;
  };

  const handleSelect = (levelIdx: number, nodeId: string) => {
    const next: (string | null)[] = [...levels];
    next[levelIdx] = nodeId || null;
    for (let i = levelIdx + 1; i < 3; i++) next[i] = null;
    setLevels(next);
    const deepest = next[2] || next[1] || next[0];
    onChange(deepest, deepest ? pathNamesFor(deepest) : []);
  };

  const selectClass = compact
    ? 'w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] bg-white'
    : 'w-full px-4 py-3 text-sm font-medium border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all shadow-sm hover:border-gray-300 bg-white';

  const optionsForLevel = (levelIdx: number): TaxonomyNode[] => {
    if (levelIdx === 0) return byParent.get('root') || [];
    const parent = levels[levelIdx - 1];
    return parent ? byParent.get(parent) || [] : [];
  };

  const rootNodes = byParent.get('root') || [];
  if (rootNodes.length === 0) return null; // portfolio has no taxonomy yet

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {[0, 1, 2].map((levelIdx) => {
        const options = optionsForLevel(levelIdx);
        if (levelIdx > 0 && (!levels[levelIdx - 1] || options.length === 0)) return null;
        return (
          <select
            key={levelIdx}
            value={levels[levelIdx] || ''}
            onChange={(e) => handleSelect(levelIdx, e.target.value)}
            className={selectClass}
          >
            <option value="">All {TAXONOMY_LEVEL_LABELS[levelIdx + 1]}s</option>
            {options.map((n) => (
              <option key={n._id} value={n._id}>
                {n.name}
              </option>
            ))}
          </select>
        );
      })}
    </div>
  );
}

export default TaxonomyCascade;
